// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * LocationHeader component for displaying location names with owner indicators.
 * Shows different styling based on location owner type.
 */
// No React import needed for TypeScript
import { Link } from '@/components/link';
import {
  ApartmentOutlined,
  VisibilityOffOutlined,
  WarehouseOutlined,
} from '@/constants/hardfin-standard-icons';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseLocation } from '@/sparse/api/types';
import { Box } from '@mui/material';

interface LocationHeaderProps {
  location: SparseLocation;
  locationOwner?: 'company' | 'customer' | 'third-party';
}

const ownerConfig = {
  company: {
    icon: WarehouseOutlined,
  },
  customer: {
    icon: ApartmentOutlined,
  },
  'third-party': {
    icon: WarehouseOutlined, // Using warehouse icon for third-party as well
  },
};

/**
 * LocationHeader component
 */
export function LocationHeader(props: LocationHeaderProps) {
  const { location, locationOwner } = props;
  const config = locationOwner ? ownerConfig[locationOwner] : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
        justifyContent: 'center',
        px: 1,
      }}
    >
      {config && (
        <config.icon
          sx={{
            fontSize: '0.875rem',
            flexShrink: 0,
            color: 'grey.600',
          }}
        />
      )}
      <Link
        to="/locations/$locationKey"
        params={{ locationKey: location.key }}
        sx={{
          fontWeight: 500,
          color: location.isArchived ? 'text.disabled' : 'rgba(0, 0, 0, 0.87)',
          fontSize: '0.875rem',
          lineHeight: 1.2,
          wordBreak: 'break-word',
          hyphens: 'auto',
          textDecoration: 'none',
          display: 'inline',
          '&:hover': {
            textDecoration: 'underline',
            textDecorationStyle: 'dashed',
          },
        }}
      >
        {getEffectiveLocationName(location)}
        {location.isArchived && (
          <VisibilityOffOutlined
            fontSize="small"
            color="disabled"
            sx={{
              fontSize: '0.75rem',
              verticalAlign: 'middle',
              ml: 0.25,
            }}
          />
        )}
      </Link>
    </Box>
  );
}
